import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import type { ColumnConfigEntry } from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import { GearSixIcon, XIcon } from '@phosphor-icons/react';
import { HeaderPageTitle } from 'src/context/header-title.context';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { MultiSelect } from 'src/components/common/MultiSelect';
import { SingleSelect } from 'src/components/common/SingleSelect';
import type { TasksQueueSearch } from './taskQueueSearch';
import {
  buildAssigneeOptions,
  buildStatusOptions,
  buildTypeOptions,
  isQueueFiltered,
  selectedOption,
  toTaskStatuses,
} from './taskQueueFilterOptions';
import { TaskGoToIdField } from './TaskGoToIdField';
import { AnchorReferenceFilters } from './AnchorReferenceFilters';
import type { AnchorReferenceColumn } from './columns/anchorReferenceColumns';
import { useQueueOutcomeOptions } from './useQueueOutcomeOptions';

export function TasksQueueToolbar({
  total,
  searchInput,
  onSearchInputChange,
  searchParams,
  updateSearchParams,
  taskTypes,
  selectedTypeId,
  users,
  agents,
  configuredColumns,
  onToggleColumn,
  onResetColumns,
  heading,
  hideTypeFilter = false,
  hideColumnsMenu = false,
  columnPicker,
  onRemoveColumn,
  referenceColumns,
}: {
  total: number;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  searchParams: TasksQueueSearch;
  updateSearchParams: (
    updates: Partial<TasksQueueSearch>,
    resetPage?: boolean,
  ) => void;
  taskTypes: TaskTypeResponse[] | undefined;
  /** The type the queue is filtered or pinned to; scopes the outcome filter. */
  selectedTypeId: string | undefined;
  users: Array<{ id: string; name: string }> | undefined;
  agents: Array<{ id: string; name: string }> | undefined;
  configuredColumns: ColumnConfigEntry[];
  onToggleColumn: (id: string, hidden: boolean) => void;
  onResetColumns: () => void;
  /** Replaces the default "Tasks" title block (task pages show their name). */
  heading?: ReactNode;
  /** Hide the type filter when the queue is pinned to one task type. */
  hideTypeFilter?: boolean;
  /** Hide the columns menu (task pages whose admin locked the columns). */
  hideColumnsMenu?: boolean;
  /** "Add column" form shown in the columns popover (task pages only). */
  columnPicker?: ReactNode;
  /** Remove a user-added path column (task pages only). */
  onRemoveColumn?: {
    canRemove: (column: ColumnConfigEntry) => boolean;
    remove: (id: string) => void;
  };
  /** Anchor columns that reference a record; each gets a filter picker. */
  referenceColumns?: readonly AnchorReferenceColumn[];
}) {
  const gt = useGT();
  const assigneeOptions = buildAssigneeOptions({
    gt,
    unassignedLabel: gt('Unassigned', {
      context: 'assignee filter, tasks with no assignee',
    }),
    users,
    agents,
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <QueueHeading heading={heading} total={total} />
      <div className="flex-1" />
      <TaskGoToIdField />
      <Input
        value={searchInput}
        onChange={(e) => onSearchInputChange(e.target.value)}
        placeholder={gt('Search tasks…', {
          context: 'search input placeholder on the tasks queue page',
        })}
        className="h-8 w-56"
      />
      <QueueTypeFilter
        hidden={hideTypeFilter}
        taskTypes={taskTypes}
        type={searchParams.type}
        updateSearchParams={updateSearchParams}
      />
      <QueueStatusFilter
        status={searchParams.status}
        updateSearchParams={updateSearchParams}
      />
      <SingleSelect
        options={assigneeOptions}
        value={selectedOption(assigneeOptions, searchParams.assignee)}
        onChange={(o) => updateSearchParams({ assignee: o?.id }, true)}
        placeholder={gt('Assignee', {
          context: 'assignee filter placeholder on the tasks queue page',
        })}
        searchPlaceholder={gt('Search assignees…', {
          context: 'search box in the tasks queue assignee filter',
        })}
        size="sm"
        shouldAutoSort={false}
        allowClear
      />
      <QueueOutcomeFilter
        taskTypes={taskTypes}
        selectedTypeId={selectedTypeId}
        outcome={searchParams.outcome}
        updateSearchParams={updateSearchParams}
      />
      <AnchorReferenceFilters
        columns={referenceColumns}
        selections={searchParams.ref}
        updateSearchParams={updateSearchParams}
      />
      <ClearFiltersButton
        searchParams={searchParams}
        onSearchInputChange={onSearchInputChange}
        updateSearchParams={updateSearchParams}
      />
      <ColumnsPopover
        hidden={hideColumnsMenu}
        columns={configuredColumns}
        onToggle={onToggleColumn}
        onReset={onResetColumns}
        picker={columnPicker}
        onRemove={onRemoveColumn}
      />
    </div>
  );
}

type UpdateQueueSearch = (
  updates: Partial<TasksQueueSearch>,
  resetPage?: boolean,
) => void;

/** Type filter; not rendered when the queue is pinned to one type. */
function QueueTypeFilter({
  hidden,
  taskTypes,
  type,
  updateSearchParams,
}: {
  hidden: boolean;
  taskTypes: TaskTypeResponse[] | undefined;
  type: string | undefined;
  updateSearchParams: UpdateQueueSearch;
}) {
  const gt = useGT();
  if (hidden) return null;
  const typeOptions = buildTypeOptions(taskTypes);
  return (
    <SingleSelect
      options={typeOptions}
      value={selectedOption(typeOptions, type)}
      onChange={(o) =>
        updateSearchParams({ type: o?.id, outcome: undefined }, true)
      }
      placeholder={gt('Type', {
        context: 'task type filter placeholder on the tasks queue page',
      })}
      size="sm"
      allowClear
    />
  );
}

function QueueStatusFilter({
  status,
  updateSearchParams,
}: {
  status: TasksQueueSearch['status'];
  updateSearchParams: UpdateQueueSearch;
}) {
  const gt = useGT();
  const statusOptions = buildStatusOptions(gt);
  const selectedStatusIds = new Set<string>(status ?? []);
  const selectedStatuses = statusOptions.filter((o) =>
    selectedStatusIds.has(o.id),
  );
  return (
    <MultiSelect
      options={statusOptions}
      value={selectedStatuses}
      onChange={(selected) =>
        updateSearchParams(
          {
            status:
              selected.length > 0
                ? toTaskStatuses(selected.map((o) => o.id))
                : undefined,
          },
          true,
        )
      }
      placeholder={gt('Status', {
        context: 'task status filter placeholder on the tasks queue page',
      })}
      showPlaceholderInSelectionText
      size="sm"
      shouldAutoSort={false}
    />
  );
}

/** Outcome filter; a selected type's custom outcomes replace the defaults. */
function QueueOutcomeFilter({
  taskTypes,
  selectedTypeId,
  outcome,
  updateSearchParams,
}: {
  taskTypes: TaskTypeResponse[] | undefined;
  selectedTypeId: string | undefined;
  outcome: string | undefined;
  updateSearchParams: UpdateQueueSearch;
}) {
  const gt = useGT();
  const outcomeOptions = useQueueOutcomeOptions({
    taskTypes,
    selectedTypeId,
    outcome,
  });
  return (
    <SingleSelect
      options={outcomeOptions}
      value={selectedOption(outcomeOptions, outcome)}
      onChange={(o) => updateSearchParams({ outcome: o?.id }, true)}
      placeholder={gt('Outcome', {
        context: 'task outcome filter placeholder on the tasks queue page',
      })}
      size="sm"
      allowClear
    />
  );
}

/** Page title + count; `/tasks` adds the "Manage types" settings link. */
function QueueHeading({
  heading,
  total,
}: {
  heading: ReactNode | undefined;
  total: number;
}) {
  if (heading) {
    return (
      <>
        {heading}
        <span className="text-xs text-muted-foreground">{total}</span>
      </>
    );
  }
  return (
    <>
      <HeaderPageTitle>
        <T context="heading on the tasks queue page">Tasks</T>
      </HeaderPageTitle>
      <span className="text-xs text-muted-foreground">{total}</span>
      <Link
        to="/settings/organization/$section"
        params={{ section: 'task-types' }}
        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        <T context="link from the tasks queue to the task type settings page">
          Manage types
        </T>
      </Link>
    </>
  );
}

/** Resets every queue filter; only shown while any filter is set. */
function ClearFiltersButton({
  searchParams,
  onSearchInputChange,
  updateSearchParams,
}: {
  searchParams: TasksQueueSearch;
  onSearchInputChange: (value: string) => void;
  updateSearchParams: (
    updates: Partial<TasksQueueSearch>,
    resetPage?: boolean,
  ) => void;
}) {
  if (!isQueueFiltered(searchParams)) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        onSearchInputChange('');
        updateSearchParams(
          {
            type: undefined,
            status: undefined,
            assignee: undefined,
            outcome: undefined,
            search: undefined,
            ref: undefined,
          },
          true,
        );
      }}
    >
      <T context="button, clear all task queue filters">Clear</T>
    </Button>
  );
}

function ColumnsPopover({
  hidden = false,
  columns,
  onToggle,
  onReset,
  picker,
  onRemove,
}: {
  /** Not rendered at all (task pages whose admin locked the columns). */
  hidden?: boolean;
  columns: ColumnConfigEntry[];
  onToggle: (id: string, hidden: boolean) => void;
  onReset: () => void;
  picker?: ReactNode;
  onRemove?: {
    canRemove: (column: ColumnConfigEntry) => boolean;
    remove: (id: string) => void;
  };
}) {
  const gt = useGT();
  if (hidden) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <GearSixIcon className="size-3.5" />
          <T context="button, open the column visibility settings">Columns</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={
          picker ? 'max-h-[80vh] w-72 overflow-y-auto p-2' : 'w-56 p-2'
        }
      >
        <div className="flex flex-col gap-1.5">
          {columns.map((column) => (
            <div key={column.id} className="flex items-center gap-1">
              <Label className="flex min-w-0 flex-1 items-center gap-2 text-xs font-normal">
                <Checkbox
                  checked={!column.hidden}
                  onCheckedChange={(checked) =>
                    onToggle(column.id, checked !== true)
                  }
                />
                <span className="truncate">{column.header}</span>
              </Label>
              {onRemove?.canRemove(column) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  aria-label={gt('Remove column {name}', {
                    context: 'button, remove a custom column from the table',
                    name: column.header,
                  })}
                  onClick={() => onRemove.remove(column.id)}
                >
                  <XIcon className="size-3" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 h-7 justify-start text-xs"
            onClick={onReset}
          >
            {gt('Reset to defaults', {
              context: 'button, reset table columns to the default set',
            })}
          </Button>
          {picker && <div className="border-t pt-2">{picker}</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
